import { createContext, useCallback, useEffect, useState } from 'react';
import { useApiClient } from '~/api-client';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { WorkspaceStore } from './workspace-store';
import { WorkspacePanelTypeEnumType, WorkspacesResource } from '@mark43/cad-api';
import { WorkspaceWindow } from '../workspace-windows/workspace-window';
import { assignIdsByTemporaryId } from './workspace-temporary-id-helpers';
import { useLocation } from 'react-router-dom';
import { useGetWorkspaceWindowIdForLocation } from './workspace-helpers';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useGetOpenPanelLimit } from './use-get-open-panel-limit';
import { useApplicationType } from '../common/hooks/use-application-type';

export const WorkspacePanelsStoreContext = createContext<WorkspaceStore | undefined>(undefined);

export function WorkspaceStoreProvider({
    children,
}: {
    children: React.ReactElement;
}): React.ReactElement {
    const apiClient = useApiClient();
    const workspace = useBootstrapState().autosaveWorkspaceDto;
    const location = useLocation();
    const { isCad } = useApplicationType();
    const getWorkspaceWindowIdForLocation = useGetWorkspaceWindowIdForLocation();
    const workspaceWindowId = isCad
        ? getWorkspaceWindowIdForLocation(location, workspace)
        : undefined;
    const { postMessage } = useBroadcast('close-panels');
    const broadcastClosePanelsOfType = useCallback(
        (workspacePanelType: WorkspacePanelTypeEnumType) => {
            if (workspaceWindowId) {
                postMessage({
                    type: 'CLOSE_PANELS',
                    payload: { workspacePanelType, fromWorkspaceWindowId: workspaceWindowId },
                });
            }
        },
        [postMessage, workspaceWindowId]
    );
    const getOpenPanelLimit = useGetOpenPanelLimit();

    const workspacePanelsStore = useState(
        () =>
            new WorkspaceStore({
                activeWorkspaceWindowId: workspaceWindowId,
                getWorkspaceWindowIdForLocation,
                broadcastClosePanelsOfType,
                getOpenPanelLimit,
                workspace,
                onSaveWorkspaceWindow: async (
                    workspacePanelsStore,
                    workspaceWindowId
                ): Promise<WorkspaceWindow | undefined> => {
                    const workspaceWindowToPersist = workspacePanelsStore
                        .getWorkspace()
                        .workspaceWindows.find(
                            (workspaceWindow) => workspaceWindow.id === workspaceWindowId
                        );

                    if (!workspaceWindowToPersist) {
                        return Promise.reject();
                    }

                    const persistablePanels = workspacePanelsStore.getPersistablePanels();
                    const panelDtos = persistablePanels
                        .filter((panel) => panel.windowId === workspaceWindowId)
                        .map((panel) => panel.getDto());

                    const payload = workspaceWindowToPersist.getDto();
                    if (panelDtos) {
                        // TODO: workspace. move workspace panel into workspace window, then workspaceWindowToPersist.getDto() will get workspacePanels dto
                        payload.workspacePanels = panelDtos;
                    }

                    return apiClient
                        .request<WorkspacesResource.UpdatePanelsForWorkspaceWindow>({
                            url: 'workspaces/window',
                            data: payload,
                            method: 'PUT',
                        })
                        .then((response) => {
                            const workspaceWindowDto = response?.data?.data;
                            if (workspaceWindowDto) {
                                workspacePanelsStore.updateWorkspaceWindowWithRemoteValues(
                                    workspaceWindowDto
                                );
                            }
                            // We check for `windowChildrenCreated` because if false then we can avoid superfluous
                            // computation
                            if (workspaceWindowDto && workspaceWindowDto.windowChildrenCreated) {
                                assignIdsByTemporaryId(
                                    workspaceWindowDto.workspacePanels,
                                    workspacePanelsStore.activePanels
                                );
                                const savedPanelsById = Object.fromEntries(
                                    workspaceWindowDto.workspacePanels.map((workspacePanel) => [
                                        workspacePanel.id,
                                        workspacePanel,
                                    ])
                                );
                                for (const workspacePanel of workspacePanelsStore.activePanels) {
                                    if (workspacePanel.queue) {
                                        const savedColumns =
                                            // any `workspacePanel` in this context has come from the backend
                                            // and can be presumed to have an id
                                            savedPanelsById[workspacePanel.id!]?.queue
                                                ?.queueColumns;
                                        if (savedColumns) {
                                            assignIdsByTemporaryId(
                                                savedColumns,
                                                workspacePanel.queue.queueColumns
                                            );
                                        }
                                    }
                                }
                            }
                            return workspaceWindowDto
                                ? workspacePanelsStore.initializeWorkspaceWindow(workspaceWindowDto)
                                : undefined;
                        });
                },
            })
    )[0];

    useBroadcast('close-panels', (message) => {
        if (message.payload.fromWorkspaceWindowId !== workspaceWindowId) {
            workspacePanelsStore.removePanelsOfType(message.payload.workspacePanelType);
        }
    });

    useEffect(() => {
        workspacePanelsStore.enqueueSaveWorkspaceWindow.flush();
        if (isCad) {
            workspacePanelsStore.setWorkspaceWindowIdForLocation(location);
        }
    }, [location, workspace, workspacePanelsStore, isCad]);

    return (
        <WorkspacePanelsStoreContext.Provider value={workspacePanelsStore}>
            {children}
        </WorkspacePanelsStoreContext.Provider>
    );
}
